import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import NoShowConfirm from "@/components/admin/NoShowConfirm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STAGES = [
  { value: "new", label: "New" },
  { value: "confirmed", label: "Confirmed" },
  { value: "active", label: "Active (Out)" },
  { value: "returned", label: "Returned" },
  { value: "closed", label: "Closed" },
];

const NO_SHOW_VALUE = "no_call_no_show";

const COLORS = {
  new: "border-white/20 text-white/80 bg-white/[0.04]",
  confirmed: "border-emerald-400/40 text-emerald-300 bg-emerald-400/[0.08]",
  active: "border-[#F2B705]/50 text-[#F2B705] bg-[#F2B705]/[0.08]",
  returned: "border-sky-400/40 text-sky-300 bg-sky-400/[0.08]",
  closed: "border-white/15 text-white/40 bg-white/[0.02]",
};

export default function LifecycleStatus({ booking, token, onAuthError, onSaved }) {
  const current = booking.lifecycle_status || "new";
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);
  const [showNoShow, setShowNoShow] = useState(false);

  // Pickup/return-date nudges so the status stays accurate without manual tracking
  const today = new Date().toISOString().slice(0, 10);
  const wr = booking.weekly_rolling || {};
  const ongoingWeekly = !!wr.enabled;
  let nudge = null;
  if ((value === "new" || value === "confirmed") && booking.pickup_date && booking.pickup_date <= today) {
    nudge = { to: "active", label: value === "new" ? "Pickup day — confirm & mark Active?" : "Pickup day — mark Active (Out)?" };
  } else if (value === "active" && ongoingWeekly) {
    // Ongoing Weekly Rental has no fixed return date — a stale/placeholder return_date must
    // never prompt "mark Returned". Only an explicit, admin-set paid-through date that has
    // actually passed can; no anchor set means this never nudges (matches rental_status.py).
    const paidThrough = (wr.paid_through || "").slice(0, 10);
    if (paidThrough && paidThrough < today) {
      nudge = { to: "returned", label: "Past paid-through date — mark Returned?" };
    }
  } else if (value === "active" && booking.return_date && booking.return_date < today) {
    nudge = { to: "returned", label: "Past return date — mark Returned?" };
  }

  // The vehicle hasn't been handed over yet — a no-show can only apply before pickup.
  const canNoShow = booking.status !== "cancelled" && !booking.no_show && (value === "new" || value === "confirmed");

  const postStatus = async (next, override) => {
    const { data } = await axios.post(`${API}/bookings/${booking.id}/status`,
      { lifecycle_status: next, override_card_check: override },
      { headers: { Authorization: `Bearer ${token}` } });
    setValue(next);
    const label = STAGES.find((s) => s.value === next)?.label || next;
    let extra = "";
    if (data.renter_notified) extra = " · customer notified";
    else if (data.vehicle_freed) extra = " · car freed" + (data.review_request_sent ? " + review email sent" : "");
    toast.success(`Status set to ${label}${extra}`);
    if (onSaved) onSaved();
  };

  const change = async (next) => {
    if (next === NO_SHOW_VALUE) { setShowNoShow(true); return; }
    if (next === value) return;
    setSaving(true);
    try {
      await postStatus(next, false);
    } catch (e) {
      if (e?.response?.status === 401 && onAuthError) { onAuthError(); setSaving(false); return; }
      const detail = e?.response?.data?.detail || "Could not update status";
      if (e?.response?.status === 400 && next === "active" && detail.toLowerCase().includes("card on file")) {
        if (window.confirm(`${detail}\n\nOverride and mark Active anyway?`)) {
          try {
            await postStatus(next, true);
          } catch (e2) {
            toast.error(e2?.response?.data?.detail || "Could not update status");
          }
        }
        setSaving(false);
        return;
      }
      toast.error(detail);
    }
    setSaving(false);
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={`booking-lifecycle-${booking.id}`}>
      <span className="text-xs text-white/40">Status</span>
      <div className="relative">
        <select
          data-testid={`booking-lifecycle-select-${booking.id}`}
          value={value}
          disabled={saving}
          onChange={(e) => change(e.target.value)}
          className={`appearance-none cursor-pointer text-xs font-semibold rounded px-3 py-1.5 pr-7 border outline-none ${COLORS[value] || COLORS.new}`}
        >
          {STAGES.map((s) => (
            <option key={s.value} value={s.value} className="bg-[#0C0C0C] text-white">{s.label}</option>
          ))}
          {canNoShow && (
            <option data-testid={`booking-lifecycle-noshow-option-${booking.id}`} value={NO_SHOW_VALUE} className="bg-[#0C0C0C] text-red-400">
              Canceled — No-call/no-show
            </option>
          )}
        </select>
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-white/40 text-[10px]">▼</span>
      </div>
      {saving && <Loader2 size={13} className="animate-spin text-white/40" />}
      {nudge && !saving && (
        <button
          data-testid={`booking-lifecycle-nudge-${booking.id}`}
          onClick={() => change(nudge.to)}
          className="text-[11px] font-semibold flex items-center gap-1 px-2 py-1 rounded border border-amber-400/50 text-amber-300 bg-amber-400/[0.1] hover:bg-amber-400 hover:text-black transition-colors animate-pulse"
          title="Click to advance the status"
        >
          ⏰ {nudge.label}
        </button>
      )}
      {showNoShow && (
        <NoShowConfirm
          booking={booking}
          token={token}
          onClose={() => setShowNoShow(false)}
          onDone={() => { setValue("closed"); onSaved?.(); }}
        />
      )}
    </div>
  );
}
